import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

import { ImpersonationAccent } from '../impersonation-confirm-modal';

/** Which glyph to draw in the icon tile. */
export type ImpersonationEmptyIcon = 'shop' | 'staff' | 'tunnel';

/**
 * Empty state for the impersonation target lists (shops / staff / tunnels).
 *
 * Consolidates the previously duplicated `app-impersonation-empty-state` (admin) and
 * `app-way-superuser-empty-state` (Way superuser), whose templates were byte-identical and whose
 * stylesheets differed only in the icon-tile and hint-pill colours. Those differences are
 * preserved through the `accent` input, so both call sites keep their exact appearance.
 *
 * @component
 * @selector app-impersonation-empty-state
 * @example
 *   <app-impersonation-empty-state
 *     title="No shops found"
 *     text="No shops match the current filters."
 *     hint="Try clearing the search."
 *     icon="shop"
 *     accent="blue"
 *   ></app-impersonation-empty-state>
 *
 * @input {string} title - Required. Headline.
 * @input {string} text - Required. Supporting copy.
 * @input {string} hint - Required. Text of the hint pill.
 * @input {ImpersonationEmptyIcon} icon - Which glyph to draw. Defaults to 'shop'.
 * @input {ImpersonationAccent} accent - Colour treatment; see ImpersonationAccent.
 * @input {string} testId - Root `data-testid`, and the stem for `<testId>-title`. Defaults to
 *   `impersonation-empty-state`.
 */
@Component({
  selector: 'app-impersonation-empty-state',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './impersonation-empty-state.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./impersonation-empty-state.component.scss'],
})
export class ImpersonationEmptyStateComponent {
  @Input({ required: true }) title!: string;
  @Input({ required: true }) text!: string;
  @Input({ required: true }) hint!: string;
  @Input() icon: ImpersonationEmptyIcon = 'shop';
  @Input() accent: ImpersonationAccent = 'neutral';
  @Input() testId = 'impersonation-empty-state';
}
